import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { APP_URL, MARKETING_URL } from "@/lib/urls";

// ── Types ────────────────────────────────────────────────────────────────────

interface EmailTemplate {
  id: string;
  key: string;
  name: string;
  description: string | null;
  subject: string;
  title: string;
  preheader: string;
  html_body: string;
  variables: string[];
  active: boolean;
  use_shell: boolean;
  current_version: number;
  updated_at: string;
  updated_by: string | null;
}

interface TemplateVersion {
  id: string;
  template_id: string;
  version: number;
  subject: string;
  title: string;
  preheader: string;
  html_body: string;
  comment: string | null;
  created_by: string;
  created_at: string;
}

interface EmailTemplateVariant {
  id: string;
  template_id: string;
  lang: string;
  subject: string;
  title: string;
  preheader: string;
  html_body: string;
  translated_by: string | null;
  translated_at: string | null;
  updated_at: string;
}

interface EmailShell {
  id: string;
  lang: string;
  html_shell: string;
  updated_at: string;
  updated_by: string | null;
}

type SaveStatus = "idle" | "changed" | "saving" | "saved";
type PanelMode  = "edit" | "preview";
type AdminTab   = "templates" | "log" | "shells";

// ── Email log types ───────────────────────────────────────────────────────────

interface EmailLogEntry {
  id: string;
  user_id: string | null;
  email_to: string;
  template: string;
  subject: string;
  resend_id: string | null;
  status: "sent" | "failed" | "skipped";
  error_message: string | null;
  created_at: string;
}

// ── Language constants ────────────────────────────────────────────────────────

const VARIANT_LANGS = ["sk", "cs", "es", "de", "fr"] as const;
const ALL_LANGS     = ["en", ...VARIANT_LANGS] as const;

const LANG_NAMES: Record<string, string> = {
  sk: "Slovak",
  cs: "Czech",
  es: "Spanish",
  de: "German",
  fr: "French",
};

const LANG_LABELS: Record<string, string> = {
  en: "English",
  sk: "Slovak",
  cs: "Czech",
  es: "Spanish",
  de: "German",
  fr: "French",
};

// ── Email log view ────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  sent:    "bg-green-100 text-green-800",
  failed:  "bg-red-100 text-red-800",
  skipped: "bg-gray-100 text-gray-600",
};

function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  const visible = local.length > 2 ? local.slice(0, 2) : local[0] ?? "";
  return `${visible}***@${domain}`;
}

function EmailLog() {
  const [entries, setEntries] = useState<EmailLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [templateFilter, setTemplateFilter] = useState<string>("all");
  const PAGE = 50;

  async function load(reset = false) {
    setLoading(true);
    const from = reset ? 0 : offset;
    let query = supabase
      .from("email_log")
      .select("id, user_id, email_to, template, subject, resend_id, status, error_message, created_at")
      .order("created_at", { ascending: false })
      .range(from, from + PAGE - 1);

    if (statusFilter !== "all") query = query.eq("status", statusFilter);
    if (templateFilter !== "all") query = query.eq("template", templateFilter);

    const { data, error } = await query;
    if (error) { toast.error("Failed to load email log"); setLoading(false); return; }
    const rows = (data ?? []) as EmailLogEntry[];
    setEntries((prev) => (reset ? rows : [...prev, ...rows]));
    setOffset(from + rows.length);
    setHasMore(rows.length === PAGE);
    setLoading(false);
  }

  useEffect(() => {
    setEntries([]);
    setOffset(0);
    void load(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, templateFilter]);

  const TEMPLATE_KEYS = [
    "email_verification", "password_reset", "magic_link",
    "subscription_upgraded", "subscription_cancelled", "subscription_downgraded",
    "audio_expiry_warning", "payment_failed",
    "invite_member", "invite_contributor",
    "referral_rewarded_referred", "referral_rewarded_referrer",
    "gift_confirmation", "gift_recipient",
  ];

  return (
    <div className="p-8 max-w-6xl space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="all">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <select
          value={templateFilter}
          onChange={(e) => setTemplateFilter(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="all">All templates</option>
          {TEMPLATE_KEYS.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        <button
          onClick={() => { setEntries([]); setOffset(0); void load(true); }}
          className="ml-auto rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading && entries.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">No emails logged yet</div>
        ) : (
          <>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 w-20">Status</th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-500 w-40">Template</th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-500 w-44">Recipient</th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-500">Subject</th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-500 w-28">Date</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/30">
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium text-[10px] ${STATUS_STYLES[e.status] ?? "bg-gray-100 text-gray-600"}`}>
                        {e.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-gray-500 truncate max-w-[160px]">
                      {e.template}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 font-mono">
                      {maskEmail(e.email_to)}
                    </td>
                    <td className="px-3 py-2.5 text-gray-700 max-w-[300px]">
                      <span className="line-clamp-1">{e.subject}</span>
                      {e.error_message && (
                        <span className="block text-red-500 text-[10px] mt-0.5 line-clamp-1">{e.error_message}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-gray-400" title={new Date(e.created_at).toLocaleString()}>
                      {relativeTime(e.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div className="px-5 py-3 border-t border-gray-100 text-center">
                <button
                  onClick={() => void load()}
                  disabled={loading}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  {loading ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Brand tokens (mirrors email-templates.ts) ─────────────────────────────────

const EMAIL_PREVIEW_WRAPPER = (title: string, preheader: string, body: string) => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${title}</title>
</head>
<body style="margin:0;padding:0;background-color:#F5F0EA;font-family:Arial,sans-serif;">
  <div style="display:none;max-height:0;overflow:hidden;">${preheader}</div>
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F5F0EA;padding:32px 16px;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" border="0"
             style="background-color:#FFFFFF;border-radius:16px;overflow:hidden;max-width:560px;width:100%;">
        <tr>
          <td style="background-color:#1A0F00;padding:28px 40px;text-align:center;">
            <table cellpadding="0" cellspacing="0" border="0" style="margin:0 auto;"><tr><td style="text-align:center;">
              <span style="font-size:28px;line-height:1;">🌰</span><br>
              <span style="font-family:Georgia,serif;font-size:22px;font-weight:bold;color:#F2A623;letter-spacing:2px;">Mori</span>
            </td></tr></table>
          </td>
        </tr>
        <tr>
          <td style="padding:40px 40px 32px;">
            <h1 style="margin:0 0 16px;font-size:22px;font-weight:500;color:#1A0F00;line-height:1.3;font-family:Georgia,serif;">${title}</h1>
            ${body}
          </td>
        </tr>
        <tr>
          <td style="background-color:#F5F0EA;padding:24px 40px;border-top:1px solid #EDE0D4;">
            <p style="margin:0 0 8px;font-size:12px;color:#888780;text-align:center;line-height:1.5;">
              You're receiving this because you have a Mori account.
            </p>
            <p style="margin:0;font-size:12px;color:#888780;text-align:center;">
              <a href="${MARKETING_URL}" style="color:#D85A30;text-decoration:none;">meetmori.app</a>
              &nbsp;·&nbsp;
              <a href="${APP_URL}/profile" style="color:#888780;text-decoration:none;">Unsubscribe</a>
            </p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Version history ───────────────────────────────────────────────────────────

function VersionViewModal({
  version,
  onClose,
}: {
  version: TemplateVersion;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"html" | "preview">("html");

  const previewHtml = EMAIL_PREVIEW_WRAPPER(version.title, version.preheader, version.html_body);
  const blobUrl = tab === "preview"
    ? URL.createObjectURL(new Blob([previewHtml], { type: "text/html" }))
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-semibold text-gray-900">v{version.version}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {version.created_by} · {new Date(version.created_at).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs">
              <button onClick={() => setTab("html")}
                className={`px-3 py-1.5 ${tab === "html" ? "bg-gray-100 font-medium text-gray-900" : "text-gray-500 hover:bg-gray-50"}`}>
                HTML
              </button>
              <button onClick={() => setTab("preview")}
                className={`px-3 py-1.5 border-l border-gray-200 ${tab === "preview" ? "bg-gray-100 font-medium text-gray-900" : "text-gray-500 hover:bg-gray-50"}`}>
                Preview
              </button>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
          </div>
        </div>
        {version.comment && (
          <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100">
            <p className="text-xs text-amber-800"><span className="font-medium">Note: </span>{version.comment}</p>
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          {tab === "html" ? (
            <div className="h-full overflow-y-auto p-5">
              <pre className="font-mono text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{version.html_body}</pre>
            </div>
          ) : (
            blobUrl && (
              <iframe
                key={blobUrl}
                src={blobUrl}
                className="w-full h-full border-0"
                sandbox="allow-same-origin"
                title="Email preview"
              />
            )
          )}
        </div>
      </div>
    </div>
  );
}

function VersionHistory({
  templateId,
  currentVersion,
  onRestore,
}: {
  templateId: string;
  currentVersion: number;
  onRestore: (v: TemplateVersion) => void;
}) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [viewing, setViewing] = useState<TemplateVersion | null>(null);
  const PAGE = 20;

  async function load(reset = false) {
    setLoading(true);
    const from = reset ? 0 : offset;
    const { data } = await supabase
      .from("email_template_versions")
      .select("id, template_id, version, subject, title, preheader, html_body, comment, created_by, created_at")
      .eq("template_id", templateId)
      .order("version", { ascending: false })
      .range(from, from + PAGE - 1);
    const rows = data ?? [];
    setVersions((prev) => (reset ? rows : [...prev, ...rows]));
    setOffset(from + rows.length);
    setHasMore(rows.length === PAGE);
    setLoading(false);
  }

  useEffect(() => {
    setVersions([]);
    setOffset(0);
    setOpen(false);
  }, [templateId]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button type="button"
        onClick={() => { if (!open && versions.length === 0) void load(true); setOpen((v) => !v); }}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">Version history</span>
          <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">v{currentVersion} current</span>
        </div>
        <span className="text-gray-400 text-sm">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {loading && versions.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
          ) : versions.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">No history yet</div>
          ) : (
            <>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-5 py-2.5 font-medium text-gray-500 w-12">Ver.</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500">Note</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500 w-28">Saved by</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500 w-24">Date</th>
                    <th className="px-5 py-2.5 w-28" />
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/30">
                      <td className="px-5 py-2.5 font-mono font-semibold text-gray-700">
                        v{v.version}
                        {v.version === currentVersion - 1 && (
                          <span className="ml-1 text-[9px] text-orange-500 font-normal">(prev)</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 max-w-[200px]">
                        <span className="line-clamp-1">{v.comment ?? "—"}</span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-400 truncate">{v.created_by}</td>
                      <td className="px-3 py-2.5 text-gray-400" title={new Date(v.created_at).toLocaleString()}>
                        {relativeTime(v.created_at)}
                      </td>
                      <td className="px-5 py-2.5">
                        <div className="flex items-center gap-2 justify-end">
                          <button onClick={() => setViewing(v)}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700">View</button>
                          <button
                            onClick={() => { onRestore(v); toast.info(`v${v.version} loaded — review and save with a comment`, { duration: 4000 }); }}
                            className="text-xs font-medium text-orange-600 hover:text-orange-700">Restore</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hasMore && (
                <div className="px-5 py-3 border-t border-gray-100 text-center">
                  <button onClick={() => void load()} disabled={loading}
                    className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50">
                    {loading ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {viewing && <VersionViewModal version={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

// ── Shell Editor ──────────────────────────────────────────────────────────────

const SHELL_SAMPLE_BODY =
  `<h1 style="margin:0 0 16px;font-size:22px;font-weight:500;color:#1A0F00;line-height:1.3;font-family:Georgia,serif;">Welcome to Mori</h1>` +
  `<p style="margin:0 0 16px;font-size:15px;line-height:1.7;color:#3D2B1F;">This is a preview of the email shell. Your template content will appear here.</p>` +
  `<a href="#" style="display:inline-block;background-color:#D85A30;color:#FFFFFF;text-decoration:none;padding:12px 28px;border-radius:8px;font-size:15px;font-weight:600;">Open Mori</a>`;

function ShellEditor() {
  const [shells, setShells] = useState<EmailShell[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLang, setSelectedLang] = useState<string | null>(null);
  const [editShell, setEditShell] = useState("");
  const [snapshot, setSnapshot] = useState("");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [mode, setMode] = useState<PanelMode>("edit");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => { void loadShells(); }, []);
  useEffect(() => { return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }; }, [previewUrl]);

  async function loadShells() {
    const { data } = await supabase
      .from("email_shells")
      .select("id, lang, html_shell, updated_at, updated_by");
    setShells((data ?? []) as EmailShell[]);
    setLoading(false);
  }

  function selectLang(lang: string) {
    const s = shells.find((x) => x.lang === lang);
    const html = s?.html_shell ?? "";
    setSelectedLang(lang);
    setEditShell(html);
    setSnapshot(html);
    setSaveStatus("idle");
    setMode("edit");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }

  const hasChanges = editShell !== snapshot;

  useEffect(() => {
    if (hasChanges && saveStatus !== "saving") setSaveStatus("changed");
  }, [editShell, hasChanges, saveStatus]);

  function openPreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const html = editShell
      .replace("{{preheader}}", "Shell preview — sample preheader text")
      .replace("{{body}}", SHELL_SAMPLE_BODY)
      .replace("{{unsubscribe_url}}", "#");
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    setPreviewUrl(url);
    setMode("preview");
  }

  async function handleSave() {
    if (!selectedLang) return;
    setSaveStatus("saving");
    const { data: { user } } = await supabase.auth.getUser();
    const adminEmail = user?.email ?? "unknown";
    const now = new Date().toISOString();

    const { error } = await supabase
      .from("email_shells")
      .upsert(
        { lang: selectedLang, html_shell: editShell, updated_at: now, updated_by: adminEmail },
        { onConflict: "lang" },
      );

    if (error) {
      toast.error("Failed to save — " + error.message);
      setSaveStatus("changed");
      return;
    }

    toast.success("Shell saved ✓");
    setSnapshot(editShell);
    setShells((prev) => {
      const exists = prev.find((s) => s.lang === selectedLang);
      if (exists) {
        return prev.map((s) =>
          s.lang === selectedLang
            ? { ...s, html_shell: editShell, updated_at: now, updated_by: adminEmail }
            : s,
        );
      }
      return [...prev, { id: "", lang: selectedLang, html_shell: editShell, updated_at: now, updated_by: adminEmail }];
    });
    setSaveStatus("saved");
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveStatus("idle"), 2000);
  }

  const selectedShell = shells.find((s) => s.lang === selectedLang);

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Left panel: language list */}
      <div className="w-[280px] shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Languages</p>
        </div>
        {ALL_LANGS.map((lang) => {
          const shell = shells.find((s) => s.lang === lang);
          return (
            <button
              key={lang}
              onClick={() => selectLang(lang)}
              className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors ${
                selectedLang === lang ? "bg-orange-50 border-l-2 border-l-orange-500" : "hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{LANG_LABELS[lang]}</span>
                <span className="text-[10px] font-mono text-gray-400 uppercase">{lang}</span>
              </div>
              {shell?.updated_by ? (
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {shell.updated_by} · {relativeTime(shell.updated_at)}
                </p>
              ) : (
                <p className="text-[10px] text-gray-300 mt-0.5">No shell saved yet</p>
              )}
            </button>
          );
        })}
      </div>

      {/* Right panel: shell editor */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 border-b border-gray-200 bg-white">
          <h1 className="text-xl font-bold text-gray-900">Shell Editor</h1>
          <p className="text-sm text-gray-500 mt-1">
            Edit the HTML wrapper (header + footer) per language. Use{" "}
            <code className="font-mono text-xs bg-gray-100 px-1 rounded">{"{{body}}"}</code>,{" "}
            <code className="font-mono text-xs bg-gray-100 px-1 rounded">{"{{preheader}}"}</code>, and{" "}
            <code className="font-mono text-xs bg-gray-100 px-1 rounded">{"{{unsubscribe_url}}"}</code> as placeholders.
          </p>
        </div>

        {!selectedLang ? (
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
            Select a language from the list
          </div>
        ) : (
          <div className="p-8 max-w-4xl space-y-5">
            {selectedShell?.updated_by && (
              <p className="text-[11px] text-gray-400">
                Last saved by {selectedShell.updated_by} · {relativeTime(selectedShell.updated_at)}
              </p>
            )}

            {/* HTML body + preview tabs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  HTML shell — {LANG_LABELS[selectedLang]}
                </label>
                <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs">
                  <button onClick={() => setMode("edit")}
                    className={`px-3 py-1.5 ${mode === "edit" ? "bg-gray-100 font-medium text-gray-900" : "text-gray-500 hover:bg-gray-50"}`}>
                    Edit
                  </button>
                  <button onClick={openPreview}
                    className={`px-3 py-1.5 border-l border-gray-200 ${mode === "preview" ? "bg-gray-100 font-medium text-gray-900" : "text-gray-500 hover:bg-gray-50"}`}>
                    Preview
                  </button>
                </div>
              </div>

              {mode === "edit" ? (
                <>
                  <textarea
                    value={editShell}
                    onChange={(e) => setEditShell(e.target.value)}
                    className="w-full min-h-[480px] rounded-md border border-gray-300 bg-white px-4 py-3 font-mono text-xs leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-400 mt-1">{editShell.length} characters</p>
                </>
              ) : (
                previewUrl && (
                  <div className="rounded-md border border-gray-200 overflow-hidden" style={{ height: 520 }}>
                    <iframe src={previewUrl} className="w-full h-full border-0" sandbox="allow-same-origin" title="Shell preview" />
                  </div>
                )
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => void handleSave()}
                disabled={!hasChanges || saveStatus === "saving"}
                className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveStatus === "saving" ? "Saving..." : "Save shell"}
              </button>
              <button
                onClick={() => { setEditShell(snapshot); setSaveStatus("idle"); setMode("edit"); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                disabled={!hasChanges || saveStatus === "saving"}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Revert changes
              </button>
              <span className="text-sm text-gray-400 ml-2">
                {saveStatus === "changed" && "Unsaved changes"}
                {saveStatus === "saving" && "Saving..."}
                {saveStatus === "saved" && "Saved ✓"}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function EmailTemplateEditor() {
  const [activeTab, setActiveTab] = useState<AdminTab>("templates");
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [mode, setMode] = useState<PanelMode>("edit");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Edit state (shared across all language tabs)
  const [editSubject,   setEditSubject]   = useState("");
  const [editTitle,     setEditTitle]     = useState("");
  const [editPreheader, setEditPreheader] = useState("");
  const [editBody,      setEditBody]      = useState("");
  const [editActive,    setEditActive]    = useState(true);
  const [editUseShell,  setEditUseShell]  = useState(true);
  const [comment,       setComment]       = useState("");
  const [commentError,  setCommentError]  = useState("");
  const [saveStatus,    setSaveStatus]    = useState<SaveStatus>("idle");
  const savedTimer = useRef<ReturnType<typeof setTimeout>>();

  type Snap = { subject: string; title: string; preheader: string; body: string; active: boolean };
  const [snapshot, setSnapshot] = useState<Snap>({ subject: "", title: "", preheader: "", body: "", active: true });

  // Language tabs + variants
  const [activeLang, setActiveLang] = useState("en");
  const [variants, setVariants] = useState<Record<string, EmailTemplateVariant>>({});

  // Shell cache (lang → html_shell string)
  const shellCache = useRef<Record<string, string>>({});

  // Translate single
  const [translating, setTranslating] = useState(false);
  const [overwriteConfirmLang, setOverwriteConfirmLang] = useState<string | null>(null);

  // Translate All modal
  const [translateAllOpen, setTranslateAllOpen] = useState(false);
  const [translateAllSelected, setTranslateAllSelected] = useState<string[]>([]);
  const [translateAllProgress, setTranslateAllProgress] = useState<string | null>(null);

  useEffect(() => { void loadTemplates(); }, []);

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  async function loadTemplates() {
    const { data, error } = await supabase
      .from("email_templates")
      .select("id, key, name, description, subject, title, preheader, html_body, variables, active, use_shell, current_version, updated_at, updated_by")
      .order("name");
    if (error) { toast.error("Failed to load templates"); return; }
    setTemplates((data ?? []) as EmailTemplate[]);
    setLoading(false);
  }

  async function selectTemplate(key: string) {
    const t = templates.find((x) => x.key === key);
    if (!t) return;

    setSelectedKey(key);
    setActiveLang("en");
    setEditSubject(t.subject);
    setEditTitle(t.title);
    setEditPreheader(t.preheader);
    setEditBody(t.html_body);
    setEditActive(t.active);
    setEditUseShell(t.use_shell ?? true);
    setSnapshot({ subject: t.subject, title: t.title, preheader: t.preheader, body: t.html_body, active: t.active });
    setSaveStatus("idle");
    setComment("");
    setCommentError("");
    setMode("edit");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);

    // Fetch variants for this template
    const { data: variantRows } = await supabase
      .from("email_template_variants")
      .select("*")
      .eq("template_id", t.id);

    const variantMap: Record<string, EmailTemplateVariant> = {};
    for (const v of (variantRows ?? []) as EmailTemplateVariant[]) {
      variantMap[v.lang] = v;
    }
    setVariants(variantMap);
  }

  function switchLang(lang: string) {
    if (lang === activeLang) return;
    setActiveLang(lang);
    setMode("edit");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setSaveStatus("idle");
    setComment("");
    setCommentError("");

    if (lang === "en") {
      const t = templates.find((x) => x.key === selectedKey);
      if (!t) return;
      setEditSubject(t.subject);
      setEditTitle(t.title);
      setEditPreheader(t.preheader);
      setEditBody(t.html_body);
      setSnapshot({ subject: t.subject, title: t.title, preheader: t.preheader, body: t.html_body, active: t.active });
    } else {
      const v = variants[lang];
      const subject   = v?.subject   ?? "";
      const title     = v?.title     ?? "";
      const preheader = v?.preheader ?? "";
      const body      = v?.html_body ?? "";
      setEditSubject(subject);
      setEditTitle(title);
      setEditPreheader(preheader);
      setEditBody(body);
      setSnapshot({ subject, title, preheader, body, active: editActive });
    }
  }

  const hasChanges =
    editSubject   !== snapshot.subject   ||
    editTitle     !== snapshot.title     ||
    editPreheader !== snapshot.preheader ||
    editBody      !== snapshot.body      ||
    editActive    !== snapshot.active;

  useEffect(() => {
    if (hasChanges && saveStatus !== "saving") setSaveStatus("changed");
  }, [editSubject, editTitle, editPreheader, editBody, editActive, hasChanges, saveStatus]);

  // ── Shell fetching ─────────────────────────────────────────────────────────

  async function fetchShellForLang(lang: string): Promise<string | null> {
    if (shellCache.current[lang]) return shellCache.current[lang];

    const { data } = await supabase
      .from("email_shells")
      .select("html_shell")
      .eq("lang", lang)
      .maybeSingle();

    if (data?.html_shell) {
      shellCache.current[lang] = data.html_shell;
      return data.html_shell;
    }

    // Fall back to EN shell
    if (lang !== "en") {
      const { data: enData } = await supabase
        .from("email_shells")
        .select("html_shell")
        .eq("lang", "en")
        .maybeSingle();
      if (enData?.html_shell) {
        shellCache.current[lang] = enData.html_shell;
        return enData.html_shell;
      }
    }

    return null;
  }

  // ── Preview ────────────────────────────────────────────────────────────────

  async function openPreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);

    let html: string;
    if (editUseShell) {
      const shell = await fetchShellForLang(activeLang);
      if (shell) {
        const bodyContent =
          `<h1 style="margin:0 0 16px;font-size:22px;font-weight:500;color:#1A0F00;line-height:1.3;font-family:Georgia,serif;">${editTitle}</h1>\n${editBody}`;
        html = shell
          .replace("{{preheader}}", editPreheader)
          .replace("{{body}}", bodyContent)
          .replace("{{unsubscribe_url}}", "#");
      } else {
        html = EMAIL_PREVIEW_WRAPPER(editTitle, editPreheader, editBody);
      }
    } else {
      // use_shell=false: html_body is the complete email
      html = editBody;
    }

    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    setPreviewUrl(url);
    setMode("preview");
  }

  // ── Save EN (existing RPC path) ────────────────────────────────────────────

  async function handleSave() {
    if (!selectedKey) return;
    if (comment.trim().length < 10) {
      setCommentError("Please describe what you changed (min 10 characters)");
      return;
    }
    setCommentError("");
    setSaveStatus("saving");

    const selected = templates.find((t) => t.key === selectedKey);
    if (!selected) return;

    const { data: { user } } = await supabase.auth.getUser();
    const adminEmail = user?.email ?? "unknown";

    const { data: newVersion, error } = await supabase.rpc("save_email_template_version", {
      p_template_id: selected.id,
      p_subject:     editSubject,
      p_title:       editTitle,
      p_preheader:   editPreheader,
      p_html_body:   editBody,
      p_comment:     comment.trim(),
      p_admin_email: adminEmail,
    });

    if (error) {
      toast.error("Failed to save — " + error.message);
      setSaveStatus("changed");
      return;
    }

    toast.success(`Saved as v${newVersion} — takes effect on next send`);
    const now = new Date().toISOString();
    const snap: Snap = { subject: editSubject, title: editTitle, preheader: editPreheader, body: editBody, active: editActive };
    setSnapshot(snap);
    setTemplates((prev) =>
      prev.map((t) =>
        t.key === selectedKey
          ? { ...t, subject: editSubject, title: editTitle, preheader: editPreheader, html_body: editBody, active: editActive, use_shell: editUseShell, updated_at: now, current_version: newVersion as number, updated_by: adminEmail }
          : t,
      ),
    );

    // active and use_shell are not handled by the RPC — update separately
    await supabase
      .from("email_templates")
      .update({ active: editActive, use_shell: editUseShell })
      .eq("key", selectedKey);

    setComment("");
    setSaveStatus("saved");
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveStatus("idle"), 2000);
  }

  // ── Save non-EN variant ────────────────────────────────────────────────────

  async function handleVariantSave() {
    if (!selectedKey || activeLang === "en") return;
    const selected = templates.find((t) => t.key === selectedKey);
    if (!selected) return;

    setSaveStatus("saving");
    const { data: { user } } = await supabase.auth.getUser();
    const adminEmail = user?.email ?? "unknown";
    const now = new Date().toISOString();

    const { error } = await supabase
      .from("email_template_variants")
      .upsert(
        {
          template_id:   selected.id,
          lang:          activeLang,
          subject:       editSubject,
          title:         editTitle,
          preheader:     editPreheader,
          html_body:     editBody,
          translated_by: adminEmail,
          translated_at: now,
          updated_at:    now,
        },
        { onConflict: "template_id,lang" },
      );

    if (error) {
      toast.error("Failed to save — " + error.message);
      setSaveStatus("changed");
      return;
    }

    toast.success("Saved ✓");
    const snap: Snap = { subject: editSubject, title: editTitle, preheader: editPreheader, body: editBody, active: editActive };
    setSnapshot(snap);
    setVariants((prev) => ({
      ...prev,
      [activeLang]: {
        id:            prev[activeLang]?.id ?? "",
        template_id:   selected.id,
        lang:          activeLang,
        subject:       editSubject,
        title:         editTitle,
        preheader:     editPreheader,
        html_body:     editBody,
        translated_by: adminEmail,
        translated_at: now,
        updated_at:    now,
      },
    }));
    setSaveStatus("saved");
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveStatus("idle"), 2000);
  }

  function handleRevert() {
    setEditSubject(snapshot.subject);
    setEditTitle(snapshot.title);
    setEditPreheader(snapshot.preheader);
    setEditBody(snapshot.body);
    setEditActive(snapshot.active);
    setComment("");
    setCommentError("");
    setSaveStatus("idle");
  }

  function handleRestore(v: TemplateVersion) {
    setEditSubject(v.subject);
    setEditTitle(v.title);
    setEditPreheader(v.preheader);
    setEditBody(v.html_body);
    setSaveStatus("changed");
    setComment("");
    setCommentError("");
  }

  // ── Send test email ────────────────────────────────────────────────────────

  const [sendingTest, setSendingTest] = useState(false);

  async function handleSendTestEmail() {
    if (!selectedKey) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Not authenticated"); return; }

    const selected = templates.find((t) => t.key === selectedKey);
    if (!selected) return;

    // Build placeholder sample data so variables render visibly in the test email
    const sampleData: Record<string, string> = {};
    for (const v of selected.variables) {
      sampleData[v] = `[${v}]`;
    }

    setSendingTest(true);
    try {
      const { data: result, error } = await supabase.functions.invoke("send-email", {
        body: {
          userId: user.id,
          template: selectedKey,
          lang: activeLang === "en" ? undefined : activeLang,
          data: sampleData,
        },
      });

      if (error) throw new Error(error.message);

      const r = result as { success?: boolean; skipped?: boolean; error?: string };
      if (r.skipped) {
        toast.warning("Email skipped — your email preferences are blocking this template type");
      } else if (r.success) {
        toast.success(`Test email sent to ${user.email}`);
      } else {
        toast.error("Send failed — " + (r.error ?? "unknown error"));
      }
    } catch (e) {
      toast.error("Failed to send — " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSendingTest(false);
    }
  }

  // ── Translation ────────────────────────────────────────────────────────────

  async function translateSingle(lang: string): Promise<void> {
    const selected = templates.find((t) => t.key === selectedKey);
    if (!selected) return;

    const { data: fnResult, error: fnErr } = await supabase.functions.invoke(
      "translate-email-template",
      { body: { templateId: selected.id, lang } },
    );

    if (fnErr) throw new Error(fnErr.message);

    const data = fnResult as {
      subject: string;
      title: string;
      preheader: string;
      html_body: string;
    };

    const now = new Date().toISOString();
    setVariants((prev) => ({
      ...prev,
      [lang]: {
        id:            prev[lang]?.id ?? "",
        template_id:   selected.id,
        lang,
        subject:       data.subject,
        title:         data.title,
        preheader:     data.preheader,
        html_body:     data.html_body,
        translated_by: "ai",
        translated_at: now,
        updated_at:    now,
      },
    }));

    // If we're currently on this lang tab, populate the edit fields
    if (activeLang === lang) {
      setEditSubject(data.subject);
      setEditTitle(data.title);
      setEditPreheader(data.preheader);
      setEditBody(data.html_body);
      setSnapshot({ subject: data.subject, title: data.title, preheader: data.preheader, body: data.html_body, active: editActive });
      setSaveStatus("idle");
    }
  }

  async function handleTranslate(lang: string, force = false) {
    const v = variants[lang];
    if (!force && v?.html_body) {
      setOverwriteConfirmLang(lang);
      return;
    }
    setTranslating(true);
    try {
      await translateSingle(lang);
      toast.success(`✨ ${LANG_NAMES[lang]} translation ready`);
    } catch (e) {
      toast.error("Translation failed — " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setTranslating(false);
    }
  }

  function openTranslateAll() {
    const missing = VARIANT_LANGS.filter((l) => !variants[l]?.html_body);
    setTranslateAllSelected([...missing]);
    setTranslateAllOpen(true);
  }

  async function runTranslateAll() {
    setTranslateAllOpen(false);
    const selected = [...translateAllSelected];
    for (let i = 0; i < selected.length; i++) {
      const lang = selected[i];
      const langName = LANG_NAMES[lang] ?? lang.toUpperCase();
      setTranslateAllProgress(`Translating ${langName}... (${i + 1}/${selected.length})`);
      try {
        await translateSingle(lang);
      } catch (e) {
        toast.error(`${langName} failed — ${e instanceof Error ? e.message : String(e)}`);
      }
    }
    setTranslateAllProgress(null);
    toast.success("All translations complete ✨");
  }

  // ── Derived ────────────────────────────────────────────────────────────────

  const selected = templates.find((t) => t.key === selectedKey);

  function langHasContent(lang: string): boolean {
    if (lang === "en") return true;
    return !!(variants[lang]?.html_body);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {/* ── Top tab bar ── */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-6 flex items-center gap-1 pt-3">
        {(["templates", "log", "shells"] as AdminTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
              activeTab === tab
                ? "bg-white border border-b-white border-gray-200 text-gray-900 -mb-px"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab === "templates" ? "Templates" : tab === "log" ? "Email Log" : "Shell Editor"}
          </button>
        ))}
      </div>

      {activeTab === "log" && <EmailLog />}

      {activeTab === "shells" && <ShellEditor />}

      {activeTab === "templates" && (
        <div className="flex flex-1 overflow-hidden">
          {/* ── Left panel: template list ── */}
          <div className="w-[280px] shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Templates ({templates.length})
              </p>
              <div className="flex items-center gap-2 text-[10px] text-gray-400">
                <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />active</span>
                <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-gray-300 inline-block" />off</span>
              </div>
            </div>

            {templates.map((t) => (
              <button key={t.key} onClick={() => void selectTemplate(t.key)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors ${
                  selectedKey === t.key ? "bg-orange-50 border-l-2 border-l-orange-500" : "hover:bg-gray-50"
                }`}>
                <div className="flex items-center gap-2">
                  <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: t.active ? "#22c55e" : "#d1d5db" }} />
                  <p className={`text-sm font-medium truncate ${t.active ? "text-gray-900" : "text-gray-400"}`}>
                    {t.name}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-0.5 pl-4">
                  <span className="text-[10px] font-mono text-gray-400 truncate max-w-[160px]">{t.key}</span>
                  <span className="text-[10px] text-gray-300 font-mono">v{t.current_version}</span>
                </div>
              </button>
            ))}
          </div>

          {/* ── Right panel: editor ── */}
          <div className="flex-1 overflow-y-auto">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-white">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h1 className="text-xl font-bold text-gray-900">Email Template Editor</h1>
                  <p className="text-sm text-gray-500 mt-1">
                    Edit email copy without redeployment. The send function prefers DB templates over hardcoded TypeScript. Every save creates a new version.
                  </p>
                </div>
                {/* Translate All button — EN tab only */}
                {selected && activeLang === "en" && (
                  <button
                    onClick={openTranslateAll}
                    disabled={!!translateAllProgress}
                    className="shrink-0 rounded-md border border-violet-200 bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {translateAllProgress ?? "✨ Translate all missing"}
                  </button>
                )}
                {/* Translate from EN — non-EN tab only */}
                {selected && activeLang !== "en" && (
                  <button
                    onClick={() => handleTranslate(activeLang)}
                    disabled={translating}
                    className="shrink-0 rounded-md border border-violet-200 bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {translating ? "Translating..." : "✨ Translate from EN"}
                  </button>
                )}
              </div>
              <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800">
                Variables like <code className="font-mono text-xs bg-amber-100 px-1 rounded">{"{{confirmUrl}}"}</code> are substituted at send time. The preview shows them literally.
              </div>
            </div>

            {!selected ? (
              <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                Select a template from the list
              </div>
            ) : (
              <div className="p-8 max-w-4xl space-y-5">
                {/* Key + active toggle */}
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Key</label>
                    <div className="font-mono text-sm bg-gray-100 rounded px-3 py-2 text-gray-700">{selected.key}</div>
                    {selected.description && (
                      <p className="text-xs text-gray-400 mt-1">{selected.description}</p>
                    )}
                    {selected.updated_by && (
                      <p className="text-[11px] text-gray-400 mt-1">
                        Last saved by {selected.updated_by} · {relativeTime(selected.updated_at)}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Active</label>
                    <button type="button" onClick={() => setEditActive((v) => !v)}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 ${editActive ? "bg-green-500" : "bg-gray-200"}`}>
                      <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${editActive ? "translate-x-7" : "translate-x-1"}`} />
                    </button>
                    <p className="text-[10px] text-gray-400 mt-1 text-center">{editActive ? "in use" : "disabled"}</p>
                  </div>
                </div>

                {/* Language tab bar */}
                <div>
                  <div className="flex items-center gap-1 border-b border-gray-200">
                    {ALL_LANGS.map((lang) => {
                      const hasContent = langHasContent(lang);
                      const isActive = activeLang === lang;
                      return (
                        <button
                          key={lang}
                          onClick={() => switchLang(lang)}
                          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                            isActive
                              ? "border-orange-500 text-gray-900"
                              : "border-transparent text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          <span
                            className="h-1.5 w-1.5 rounded-full shrink-0"
                            style={{ backgroundColor: hasContent ? "#22c55e" : "#d1d5db" }}
                          />
                          {lang.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Shell toggle */}
                <div className="flex items-center gap-3 py-1">
                  <button
                    type="button"
                    onClick={() => setEditUseShell((v) => !v)}
                    className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 ${editUseShell ? "bg-orange-500" : "bg-gray-200"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${editUseShell ? "translate-x-5" : "translate-x-1"}`} />
                  </button>
                  <span className="text-sm text-gray-600">
                    Use default shell (header + footer)
                  </span>
                  {!editUseShell && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                      Preview renders html_body as complete email
                    </span>
                  )}
                </div>

                {/* Variables badge list */}
                {selected.variables.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Variables</label>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.variables.map((v) => (
                        <span key={v} className="font-mono text-xs bg-blue-50 text-blue-700 border border-blue-100 rounded px-2 py-0.5">
                          {`{{${v}}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Non-EN info badge */}
                {activeLang !== "en" && variants[activeLang]?.translated_by === "ai" && (
                  <div className="rounded-md bg-violet-50 border border-violet-200 px-4 py-2.5 text-xs text-violet-700">
                    AI-translated · {variants[activeLang]?.translated_at
                      ? relativeTime(variants[activeLang]!.translated_at!)
                      : ""}
                    {" "}— review before sending to users.
                  </div>
                )}

                {/* Subject */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Subject line</label>
                  <input type="text" value={editSubject} onChange={(e) => setEditSubject(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
                </div>

                {/* Title + Preheader row */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Email title (h1)</label>
                    <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Preheader</label>
                    <input type="text" value={editPreheader} onChange={(e) => setEditPreheader(e.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
                  </div>
                </div>

                {/* HTML body + preview tabs */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">HTML body</label>
                    <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs">
                      <button onClick={() => setMode("edit")}
                        className={`px-3 py-1.5 ${mode === "edit" ? "bg-gray-100 font-medium text-gray-900" : "text-gray-500 hover:bg-gray-50"}`}>
                        Edit
                      </button>
                      <button onClick={() => void openPreview()}
                        className={`px-3 py-1.5 border-l border-gray-200 ${mode === "preview" ? "bg-gray-100 font-medium text-gray-900" : "text-gray-500 hover:bg-gray-50"}`}>
                        Preview
                      </button>
                    </div>
                  </div>

                  {mode === "edit" ? (
                    <>
                      <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)}
                        className="w-full min-h-[360px] rounded-md border border-gray-300 bg-white px-4 py-3 font-mono text-xs leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
                      <p className="text-xs text-gray-400 mt-1">{editBody.length} characters</p>
                    </>
                  ) : (
                    previewUrl && (
                      <div className="rounded-md border border-gray-200 overflow-hidden" style={{ height: 520 }}>
                        <iframe src={previewUrl} className="w-full h-full border-0" sandbox="allow-same-origin" title="Email preview" />
                      </div>
                    )
                  )}
                </div>

                {/* EN-only: comment + version history */}
                {activeLang === "en" && (
                  <>
                    {/* Comment field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        What changed?
                        <span className="ml-1 text-red-400 font-normal normal-case">required</span>
                      </label>
                      <input type="text" value={comment}
                        onChange={(e) => { setComment(e.target.value); if (commentError && e.target.value.trim().length >= 10) setCommentError(""); }}
                        placeholder="e.g. Updated CTA copy for the invite email"
                        className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${commentError ? "border-red-400 bg-red-50" : "border-gray-300 bg-white"}`} />
                      {commentError ? (
                        <p className="text-xs text-red-500 mt-1">{commentError}</p>
                      ) : (
                        <p className="text-xs text-gray-400 mt-1">{comment.trim().length}/10 minimum · Appears in version history</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3">
                      <button onClick={() => void handleSave()} disabled={!hasChanges || saveStatus === "saving"}
                        className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        {saveStatus === "saving" ? "Saving..." : `Save as v${selected.current_version + 1}`}
                      </button>
                      <button onClick={handleRevert} disabled={!hasChanges || saveStatus === "saving"}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        Revert changes
                      </button>
                      <span className="text-sm text-gray-400 ml-2">
                        {saveStatus === "changed" && "Unsaved changes"}
                        {saveStatus === "saving" && "Saving..."}
                        {saveStatus === "saved" && "Saved ✓"}
                      </span>
                      <button
                        onClick={() => void handleSendTestEmail()}
                        disabled={sendingTest}
                        className="ml-auto rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {sendingTest ? "Sending..." : "Send test to me"}
                      </button>
                    </div>

                    {/* Version history */}
                    <VersionHistory
                      templateId={selected.id}
                      currentVersion={selected.current_version}
                      onRestore={handleRestore}
                    />
                  </>
                )}

                {/* Non-EN actions */}
                {activeLang !== "en" && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => void handleVariantSave()}
                      disabled={!hasChanges || saveStatus === "saving"}
                      className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saveStatus === "saving" ? "Saving..." : `Save ${activeLang.toUpperCase()}`}
                    </button>
                    <button
                      onClick={handleRevert}
                      disabled={!hasChanges || saveStatus === "saving"}
                      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Revert changes
                    </button>
                    <span className="text-sm text-gray-400 ml-2">
                      {saveStatus === "changed" && "Unsaved changes"}
                      {saveStatus === "saving" && "Saving..."}
                      {saveStatus === "saved" && "Saved ✓"}
                    </span>
                    <button
                      onClick={() => void handleSendTestEmail()}
                      disabled={sendingTest}
                      className="ml-auto rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {sendingTest ? "Sending..." : "Send test to me"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Overwrite confirmation dialog ── */}
      {overwriteConfirmLang && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Overwrite existing translation?</h2>
            <p className="text-sm text-gray-600 mb-5">
              {LANG_LABELS[overwriteConfirmLang]} already has content. Overwrite with a fresh AI translation?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setOverwriteConfirmLang(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => { const lang = overwriteConfirmLang; setOverwriteConfirmLang(null); void handleTranslate(lang, true); }}
                className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
              >
                Overwrite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Translate All modal ── */}
      {translateAllOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Translate all missing languages</h2>
            <p className="text-xs text-gray-500 mb-4">Missing languages are pre-selected. Uncheck to skip.</p>
            <div className="space-y-2 mb-5">
              {VARIANT_LANGS.map((lang) => {
                const hasContent = langHasContent(lang);
                const checked = translateAllSelected.includes(lang);
                return (
                  <label key={lang} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setTranslateAllSelected((prev) =>
                          e.target.checked ? [...prev, lang] : prev.filter((l) => l !== lang),
                        );
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-800 flex-1">{LANG_LABELS[lang]}</span>
                    {hasContent ? (
                      <span className="text-[10px] text-green-600 font-medium">exists</span>
                    ) : (
                      <span className="text-[10px] text-gray-400">missing</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setTranslateAllOpen(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => void runTranslateAll()}
                disabled={translateAllSelected.length === 0}
                className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Translate ({translateAllSelected.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
